import { observer } from "mobx-react";
import { Alert } from "../../components-v2/Alert";
import { SendInvitationDialogStore } from "../../stores/SendInvitationDialogStore";
import { Dialog } from "../../components-v2/Dialog";
import { MTextInput } from "../../components-v2/TextInput";
import { Box, VBox } from "../../components-v2/Box";
import { useTranslation } from "../../stores/RootStoreContext";
import { MRadioButtonGroup } from "../../components-v2/RadioButton";
import { LinkButton } from "../../components-v2/Button";
import { Language } from "../../i18n/languages";
import { LocalizedTextEditor } from "../../text/LocalizedTextEditor";
import { Markdown } from "../../text/Markdown";
import { isProductionEnvironment } from "../../utils/environments";

export const SendInvitationDialog = observer(function InviteUserDialog({
  store,
}: {
  store: SendInvitationDialogStore;
}) {
  const { t } = useTranslation();

  const handleSubmit = store.showSubmitButton ? store.submitInviteUserDialog : undefined;
  return (
    <Dialog
      title={store.title}
      isSubmitting={store.isLoading}
      submitLabel={t("invite-user.send-invite")}
      cancelLabel={t(store.closeLabel)}
      onSubmit={handleSubmit}
      onCancel={store.onClose}
    >
      <VBox gap={"md"}>
        {!isProductionEnvironment() && store.adminInfo && <Markdown value={t(store.adminInfo)} />}
        {store.description && <Box>{t(store.description)}</Box>}
        <ShareholderInvitationsPersonalNote store={store} />
        {store.error && <Alert type={"error"} message={<>{store.error}</>} />}
      </VBox>
    </Dialog>
  );
});

const ShareholderInvitationsPersonalNote = observer(
  ({ store }: { store: SendInvitationDialogStore }) => {
    const { t } = useTranslation();
    const en = useTranslation(Language.EN);
    const no = useTranslation(Language.NO);
    return (
      <VBox>
        <Box gap={"md"} marginBottom={"md"}>
          <div>{t("shareholders.notify.email.language")}</div>
          <MRadioButtonGroup model={store.language} horizontal />
        </Box>
        <strong>{t("invite.user.email.title")}</strong>
        <i>
          {store.language.value === Language.NO
            ? no.t(store.commonEmailText)
            : en.t(store.commonEmailText)}
        </i>
        <Box
          justifyContent={store.isPersonalNoteEnabled ? "space-between" : "start"}
          marginTop={"md"}
        >
          {store.isPersonalNoteEnabled && <div>{t("invite-user.personal.note")}</div>}
          <LinkButton
            label={store.isPersonalNoteEnabled ? t("remove") : t("invite-user.add.personal.note")}
            icon={store.isPersonalNoteEnabled ? "trash" : "plus"}
            onClick={store.togglePersonalNote}
          />
        </Box>
        {store.isPersonalNoteEnabled && (
          <LocalizedTextEditor model={store.personalNote} autofocus />
        )}
        <VBox marginTop={"md"}>
          <strong>{t("general-meeting.resendNotice.email.label")}</strong>
          <MTextInput model={store.email} label={t("invite-user.email")} />
        </VBox>
      </VBox>
    );
  },
);
